// What the drawn status bar shows. The clock is always the real time of the
// capture and the battery is whatever the user sets; with `vary` on, the
// network and phone style are drawn at random so downloads do not all look
// like the same phone.

const between = (min, max) => min + Math.floor(Math.random() * (max - min + 1));

// Weighted pick: [value, weight] pairs.
const weighted = (pairs) => {
  const total = pairs.reduce((sum, [, weight]) => sum + weight, 0);
  let roll = Math.random() * total;
  for (const [value, weight] of pairs) {
    roll -= weight;
    if (roll < 0) return value;
  }
  return pairs[0][0];
};

export const LOW_BATTERY = 15;
export const DEFAULT_BATTERY = 78;

// How each vendor's status bar differs, as far as the frame draws it: the
// signal and battery glyphs, where the battery percentage sits, and the extras
// (alarm, VoLTE, live network speed) that some skins show. Pixel is the
// default and matches a real Pixel screenshot.
const DEVICE_LOOKS = [
  { id: "pixel", signal: "triangle", battery: "vertical", percent: "after", alarm: true, volte: true, speed: false },
  { id: "samsung", signal: "bars", battery: "horizontal", percent: "before", alarm: true, volte: true, speed: false },
  { id: "xiaomi", signal: "bars", battery: "horizontal", percent: "after", alarm: false, volte: true, speed: true },
  { id: "oppo", signal: "bars", battery: "horizontal", percent: "none", alarm: false, volte: false, speed: false },
];

const PIXEL = DEVICE_LOOKS[0];

const formatClock = (now) =>
  `${now.getHours() % 12 || 12}:${`${now.getMinutes()}`.padStart(2, "0")}`;

const randomLook = () =>
  weighted([
    [DEVICE_LOOKS[0], 50],
    [DEVICE_LOOKS[1], 20],
    [DEVICE_LOOKS[2], 20],
    [DEVICE_LOOKS[3], 10],
  ]);

const randomNetwork = () =>
  weighted([
    [{ kind: "cell", label: "5G" }, 35],
    [{ kind: "cell", label: "4G" }, 25],
    [{ kind: "cell", label: "4G+" }, 8],
    [{ kind: "cell", label: "H+" }, 4],
    [{ kind: "wifi" }, 28],
  ]);

// An empty or non-numeric box falls back to the default rather than 0.
const clampLevel = (value) => {
  const level = Math.round(parseFloat(value));
  return Number.isFinite(level) ? Math.min(Math.max(level, 1), 100) : DEFAULT_BATTERY;
};

/**
 * @param {object}  options
 * @param {boolean} options.vary      random network and phone style when true;
 *                                    a fixed Pixel on 5G when false.
 * @param {number}  options.level     battery percent, 1-100.
 * @param {boolean} options.charging  draw the charging bolt.
 */
export const makeDeviceStatus = ({
  vary = true,
  level = DEFAULT_BATTERY,
  charging = false,
  now = new Date(),
} = {}) => ({
  time: formatClock(now),
  look: vary ? randomLook() : PIXEL,
  battery: { level: clampLevel(level), charging },
  network: vary ? randomNetwork() : { kind: "cell", label: "5G" },
  bars: vary ? weighted([[4, 50], [3, 30], [2, 20]]) : 4,
  // Shown by skins that draw it, as the app's own users see it on a mobile
  // connection: a few hundred KB/s.
  speed: `${between(200, 400)}K/s`,
});
